import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SpaceDiscriminator, SpaceReadModel } from '@client/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DD,
  DT,
  GridCol,
  GridRow,
  isDefinedNumber,
  isDefinedString,
  toNumberString,
  toPriceWithIntervalString,
} from '@client/toolkit';

export interface SpaceInformationCardProps {
  className?: string;
  space?: SpaceReadModel;
  discriminator?: SpaceDiscriminator;
  isFetching?: boolean;
}

export function SpaceInformationCard({
  className,
  space,
  discriminator = space?.discriminator || undefined,
  isFetching,
}: SpaceInformationCardProps) {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tType } = useTranslation('type');
  const { t: tGlobal } = useTranslation('global');

  const title = useMemo(() => {
    if (discriminator === 'Generic') return t('generic.genericInformation');
    return discriminator === 'ResidentialUnit' ? t('unit.unitInformation') : t('space.spaceInformation');
  }, [discriminator, t]);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex-1" variant="dense" loading={!ready}>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex flex-col py-2">
            <GridRow cols={2} spacingY={1} spacingX={2}>
              <GridCol span={1}>
                <DT className="w-1/2">{tGlobal('space.area')}</DT>
              </GridCol>
              <GridCol span={1}>
                <DD loading={isFetching} isEmpty={!isDefinedNumber(space?.area?.metric)}>
                  {toNumberString(space?.area?.metric ?? undefined, undefined, {
                    unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                  })}
                </DD>
              </GridCol>

              {(discriminator === 'Generic' || discriminator === 'CommercialSpace') && (
                <>
                  <GridCol span={1}>
                    <DT className="w-1/2">{tGlobal('building.assetClass')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!space?.assetClass}>
                      {space?.assetClass ? tType(`AssetClass.${space.assetClass}`) : null}
                    </DD>
                  </GridCol>
                </>
              )}

              <GridCol span={1}>
                <DT className="w-1/2">{tGlobal('space.status')}</DT>
              </GridCol>
              <GridCol span={1}>
                <DD loading={isFetching} isEmpty={!space?.status}>
                  <span style={space?.status === 'Rented' ? { color: '#10b981' } : undefined}>
                    {tType(`VacancyStatus.${space?.status}`)}
                  </span>
                </DD>
              </GridCol>

              {/* COMMENTED OUT: Brukertype - uncomment to restore
              <GridCol span={1}>
                <DT>{tGlobal('space.usageType')}</DT>
              </GridCol>
              <GridCol span={1}>
                <DD
                  loading={isFetching}
                  isEmpty={!isDefinedString(space?.usageType)}
                  variantColor="none"
                  className="text-brand-primary"
                >
                  {space?.usageType}
                </DD>
              </GridCol>
              */}

              {/* COMMENTED OUT: Standard leie - uncomment to restore
              <GridCol span={1}>
                <DT>{tGlobal('space.rent')}</DT>
              </GridCol>
              <GridCol span={1}>
                <DD loading={isFetching} isEmpty={!isDefinedString(toPriceWithIntervalString(space?.rent))}>
                  {toPriceWithIntervalString(space?.rent)}
                </DD>
              </GridCol>
              */}
            </GridRow>
          </div>

          <div className="flex flex-col py-2">
            <DT className="mb-2">{tGlobal('space.description')}</DT>
            <DD
              loading={isFetching}
              isEmpty={!isDefinedString(space?.description)}
              className="text-sm text-white/60"
              variantFontSize="none"
            >
              {space?.description}
            </DD>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
